/**
 * A unique ID for referring to a client of the bridge API.
 *
 * @description Can either be a producer or receiver of events.
 */
export type ClientId = string & { __clientId: symbol };

/**
 * Convert a string reference to a {@link ClientId}.
 * @param s
 * @returns client ID reference
 */
export const client = (s: string) => s as ClientId;

export type PendingClient = {
	/**
	 * A unique ID for the given client.
	 */
	id: ClientId;
	/**
	 * Marks that the client has yet to be seen as initialised by this bridge API instance.
	 *
	 * @description Consider scenarios where the receiver has not mounted yet. Every client therefore MUST
	 * perform a handshake with a client they are intending to speak with. TODO - document full flow in CNS-18261.
	 */
	state: 'pending';
};

export type ReadyLocalClient = {
	/**
	 * A unique ID for the given client.
	 */
	id: ClientId;
	/**
	 * Indicates the current client is a local client, which can be immediately invoked in this JS context.
	 */
	type: 'local';
	/**
	 * Marks that the client is ready to start sending and handling commands.
	 */
	state: 'ready';
};

export type ReadyRemoteClient = {
	/**
	 * A unique ID for the given client.
	 */
	id: ClientId;
	/**
	 * Indicates the current client is a remote client, which can needs to be communicated with asynchronously via messages.
	 */
	type: 'remote';
	/**
	 * Marks that the client is ready to start sending and handling commands.
	 */
	state: 'ready';
};

/**
 * Current state of a client. A client can either be:
 *
 * - Pending: still awaiting handshake from the current JS context & a specific sender ID
 * - Ready: handshake succeeded between sender & client - ready to communicate ✅
 *
 * Additionally, clients can be `local` or `remote`. A local client is one which can be immediately
 * invoked from the current JS context (e.g., a same-origin `iframe`). A remote client is one which
 * relies upon asynchronous message passing to communicate with the current JS context (e.g., a cross-
 * origin iframe which needs to be communicated with via {@link window.postMessage}).
 */
export type ClientState = PendingClient | ReadyLocalClient | ReadyRemoteClient;
